import { Button, Card, DialogContent, MobileStepper } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { closeDrawer, DrawerStateKinds, updateTripRouteId, useDrawerState } from "../../features/Drawer/drawerSlice";
import {
    setSelectedRoute,
    setWilmaState,
    useRiskViewerStateKind,
    useRoutesToDisplay,
    useSelectedRoute
} from "../../features/riskViewer/riskViewerSlice";
import MobileNavBar from "../tripSidebar/MobileNavBar";
import { useIsMobile } from "../../features/pageWidth/pageWidthSlice";
import { useDispatch } from "react-redux";
import { setSelectedTime } from "../../features/surface/surfaceSlice";
import { ComputationStatus, RouteRequestResult } from "../../generated/gpb/api/route";
import { editTrip, getProcessingRequestStatus, sortByFinalScore } from "../trips/tripUtils";
import useIdentity from "../../app/auth";
import AdminControls from "../metric/AdminControls";
import { offWhite } from "./MutistartDrawerStyles";
import TripHeader from "../metric/TripHeader";
import { RouteId } from "../../generated/gpb/api/id";
import MultistartCard from "./MultistartCard";
import { useUserViewOverride } from "../../features/riskViewer/userViewOverrideSlice";
import { NO_ALTERNATIVE_ROUTES_TEXT, REQUEST_DETAILS, YOUR_REQUEST_MESSAGE } from "../UserVisibleStrings";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { useSwipeable } from "react-swipeable";
import { generateErrorContent, getRouteInputs } from "../metric/RouteUtils";
import SegmentMetricTable from "../metric/SegmentMetricTable";
import { useGetRouteMetricsAndInstructionsQuery } from "../../services/routing";
import { skipToken } from "@reduxjs/toolkit/dist/query/react";


function getMultistartCardSet( sortedRouteResults: Array<RouteRequestResultAndLabel>, selectedRoute: RouteId | undefined )
{
    return (
        sortedRouteResults?.map( ( result, index ) =>
        {
            if ( result !== undefined )
            {
                return (
                    <Card
                        sx = {{
                            backgroundColor: offWhite,
                            borderRadius: "0px",
                            display: "flex",
                            justifyContent: "center",
                        }}
                        key = {"impact-card-" + index}
                    >
                        <MultistartCard
                            result = {result.element}
                            isSelected = {result.element.routeId?.hexString === selectedRoute?.hexString}
                            showButtons
                            label = {result.label}
                        />
                    </Card>
                );
            }
        } )
    );
}
export type RouteRequestResultAndLabel = {
    element: RouteRequestResult,
    label: string,
};
export default function MultistartSelectionDrawer( )
{
    const drawerState = useDrawerState();
    const tripData = drawerState.kind === DrawerStateKinds.MultistartSelection ? drawerState.trip : undefined;
    const routeId = drawerState.kind === DrawerStateKinds.MultistartSelection ? drawerState.routeId : undefined;
    const isMobile = useIsMobile();
    const dispatch = useDispatch();
    const riskViewerStateKind = useRiskViewerStateKind();
    const routesToDisplay = useRoutesToDisplay();
    const selectedRouteId = useSelectedRoute();

    const userViewOverride = useUserViewOverride();
    const { isAdmin } = useIdentity( userViewOverride );

    const routeIsErrored = getProcessingRequestStatus( tripData ) === ComputationStatus.ERROR;
    const [ openHelp, setOpenHelp ] = useState( false );


    const {
        data: metricsAndInstructions,
    } = useGetRouteMetricsAndInstructionsQuery( routeId?.hexString ?? skipToken );

    const sortedRouteResults = useMemo( () =>
    {
        if ( !routesToDisplay ) return [];
        return sortByFinalScore( metricsAndInstructions,
            routesToDisplay.filter( ( result ): result is RouteRequestResult => result !== undefined ),
            "asc" );
    }, [ routesToDisplay ] );

    function handleEditClick()
    {
        editTrip( dispatch, tripData );
    }

    const carouselResponse = ( now: number | undefined ) =>
    {
        const newSelectedRouteId = sortedRouteResults[now !== undefined ? now : 0].element.routeId;
        dispatch( setSelectedRoute( newSelectedRouteId ) );
        dispatch( updateTripRouteId( { routeId: newSelectedRouteId } ) );
    };

    const multistartCards = getMultistartCardSet( sortedRouteResults, routeId );
    const [ activeStep, setActiveStep ] =
        useState( sortedRouteResults.findIndex( route => route.element.routeId?.hexString === selectedRouteId?.hexString ) );

    const handleNext = () =>
    {
        const newStep = Math.min( activeStep + 1, multistartCards.length - 1 );
        setActiveStep( newStep );
        carouselResponse( newStep );
    };

    const handleBack = () =>
    {
        const newStep = Math.max( activeStep - 1, 0 );
        setActiveStep( newStep );
        carouselResponse( newStep );
    };

    useEffect( () =>
    {
        setActiveStep( sortedRouteResults.findIndex( route => route.element.routeId?.hexString === selectedRouteId?.hexString ) );
    }, [ selectedRouteId, sortedRouteResults ] );

    const swipeHandlers = useSwipeable( {
        onSwipedLeft: handleNext,
        onSwipedRight: handleBack,
    } );

    return (
        <div style = {{
            backgroundColor: offWhite,
            display: "flex",
            flexDirection: "column",
            height: "100dvh",
            overflow: "auto"
        }} {...swipeHandlers}
        >
            <TripHeader
                tripData = {tripData}
                isAdmin = {isAdmin}
                isMissingRoutes = {false} //should probably remove this or figure out an alternative way of getting this info.
                handleEditClick = {handleEditClick}
                dispatch = {dispatch}
                riskViewerStateKind = {riskViewerStateKind}
            />
            <div style = {{ flex: 1, overflowY: "auto" }}>
                <DialogContent>
                    { isAdmin && tripData !== undefined ? (
                        <AdminControls/> )
                        : undefined }
                    {multistartCards.length === 1 &&
                    <div>
                        {NO_ALTERNATIVE_ROUTES_TEXT}
                    </div>}
                </DialogContent>

                {!routeIsErrored && isMobile ? (
                    <React.Fragment>
                        <div style = {{ padding: "16px 0" }}>
                            {multistartCards[activeStep]}
                        </div>
                        {multistartCards.length > 1 && (
                            <MobileStepper
                                steps = {multistartCards.length}
                                position = "static"
                                activeStep = {activeStep}
                                nextButton = {
                                    <Button
                                        size = "small"
                                        onClick = {handleNext}
                                        disabled = {activeStep === multistartCards.length - 1}
                                    >
                                        Next
                                        <KeyboardArrowRight/>
                                    </Button>
                                }
                                backButton = {
                                    <Button
                                        size = "small"
                                        onClick = {handleBack}
                                        disabled = {activeStep === 0}
                                    >
                                        <KeyboardArrowLeft/>
                                        Back
                                    </Button>
                                }
                            />
                        )}
                    </React.Fragment>
                ) : (
                    routeIsErrored
                        ? (
                            <div>
                                <div>{multistartCards}</div>
                                {generateErrorContent( undefined, tripData, openHelp, setOpenHelp ) }
                                <div>{YOUR_REQUEST_MESSAGE}</div>
                                <hr style = {{ borderTop: "1px dotted #999", margin: "10px 0" }}/>
                                <SegmentMetricTable
                                    routeMetrics = {getRouteInputs( tripData, undefined )}
                                    heading = {REQUEST_DETAILS}
                                />
                            </div> )
                        : ( <div>{multistartCards}</div> )
                )}
            </div>

            {isMobile && (
                <MobileNavBar
                    onClose = {() =>
                    {
                        dispatch( setWilmaState() );
                        dispatch( setSelectedTime( Date.now() ) );
                        dispatch( closeDrawer() );
                    }}
                />
            )}
        </div>
    );
}
